import { FC } from 'react';
import classNames from 'classnames';

import lang from '../../../../lib/language';
import { Earning } from '../../../../lib/models/fundamental/types';

import CloseIcon from '../../../../assets/img/close-line.svg';
import CompanyLogo from '../../../../components/CompanyLogo/CompanyLogo';
import BaseModal from '../../../BaseModal/BaseModal';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileDrawer from '../../../MobileDrawer/MobileDrawer';

import CurrentReportCard from './CurrentReportCard';
import PreviousReportsSection from './PreviousReportsSection';

import styles from './EarningsReportDetails.module.scss';

export type EarningsReportDetailsProps = {
  earning: Earning;
  /** Resolved by the wrapper from its own store. */
  logo?: string;
  logoUrl?: string;
  companyName?: string;
  /** Fetched newest-first by the wrapper, passed here oldest-first. */
  previousReports: Earning[];
  isLoadingPrevious: boolean;
  onClose: () => void;
};

const EarningsReportDetails: FC<EarningsReportDetailsProps> = ({
  earning,
  logo,
  logoUrl,
  companyName,
  previousReports,
  isLoadingPrevious,
  onClose,
}: EarningsReportDetailsProps): JSX.Element => {
  const { isMobile } = useIsMobile();

  const content = (
    <div className={classNames(styles.container, { [styles.modalCard]: !isMobile })}>
      <div className={styles.header}>
        {(logoUrl || logo) && (
          <CompanyLogo
            url={logoUrl || ''}
            logo={logo || ''}
            symbol={earning.symbol}
            className={styles.logo}
          />
        )}
        <div className={styles.titleWrapper}>
          <div className={styles.title}>{lang.earningsReportDetailsTitle(earning.symbol)}</div>
          <div className={styles.companyName}>{companyName}</div>
        </div>
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label={lang.commonClose()}>
          <img src={CloseIcon} alt="" />
        </button>
      </div>
      <CurrentReportCard earning={earning} />
      <PreviousReportsSection reports={previousReports} isLoading={isLoadingPrevious} />
    </div>
  );

  if (isMobile) {
    return (
      <MobileDrawer isOpen title="" showTitle={false} showCloseButton={false} onClose={onClose}>
        <div className={styles.drawerContent}>
          {content}
          <div className={styles.drawerIndicator} aria-hidden="true" />
        </div>
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen toggle={onClose} noPadding>
      {content}
    </BaseModal>
  );
};

export default EarningsReportDetails;
